import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useMemo, useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import {
  agentPaysMerchantFee,
  isSmsfCardDisabled,
  useGetGeneralDataQuery,
} from '@/features/auth';
import {
  isStripeConfigured,
  sortCardsByDefault,
  useGetSubscriptionQuery,
  usePayAnnualReviewMutation,
  usePayInvoiceMutation,
  type Card,
  type Subscription,
} from '@/features/payments';
import { toastShown } from '@/features/ui';

import { isSuperFund } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import {
  invoiceLinksFor,
  paymentLinesFor,
  totalOf,
  type InvoiceLink,
  type PaymentLine,
} from './paymentLines';
import {
  agentAbsorbsFee,
  MINIMUM_CHARGE,
  processingFeeOn,
  type PaymentMethod,
} from './processingFee';
import { paymentRouteOf } from './rowActions';
import {
  confirmBankTransfer,
  needsClientConfirmation,
  paymentMethodValueOf,
  PaymentError,
  tokeniseNewCard,
  type BankTransferDetails,
} from './submitPayment';
import { useMethodCompletion, type MethodCompletion } from './useMethodCompletion';

/** What each method costs the director in merchant fees, shown on its own row
 *  before they pick one. */
export interface MethodFees {
  card: number;
  bankTransfer: number;
}

interface UsePayItem {
  pay: () => Promise<void>;
  isSubmitting: boolean;
  error: string;
  cards: Card[];
  isLoadingCards: boolean;
  selectedCard: string;
  selectCard: (cardId: string) => void;
  method: PaymentMethod;
  selectMethod: (method: PaymentMethod) => void;
  /** True when the ATO's SMSF rule closes off both card methods. */
  isCardDisabled: boolean;
  cardholderName: string;
  setCardholderName: (name: string) => void;
  saveCard: boolean;
  setSaveCard: (save: boolean) => void;
  bankTransfer: BankTransferDetails;
  setBankTransfer: (details: BankTransferDetails) => void;
  lines: PaymentLine[];
  invoiceLinks: InvoiceLink[];
  /** What is owed before the merchant fee. */
  subtotal: number;
  /** The fee on the chosen method, already included in `total`. */
  processingFee: number;
  total: number;
  fees: MethodFees;
  canPay: boolean;
  /** Passed to the Stripe Elements so they can report themselves complete. */
  completion: MethodCompletion;
}

/**
 * The card list and which one is selected.
 *
 * Pure, and separate from the hook: resolving "the director's choice, else the
 * default, else the first" is three fallbacks that have nothing to do with
 * charging, and inlining them pushed the hook past the complexity limit.
 */
const resolveCards = (
  subscription: Subscription | undefined,
  chosenCard: string,
): { cards: Card[]; selectedCard: string } => {
  const customerCards = subscription?.customerCards;
  const defaultCard = customerCards?.defaultCard ?? '';
  // Default card first: on a phone the select opens as a full-height list, and
  // the card the director actually uses should not be somewhere down it.
  const cards = sortCardsByDefault(customerCards?.allCards ?? [], defaultCard);

  if (chosenCard !== '') return { cards, selectedCard: chosenCard };
  if (defaultCard !== '') return { cards, selectedCard: defaultCard };
  return { cards, selectedCard: cards[0]?.id ?? '' };
};

const EMAIL_PATTERN = /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/;

/**
 * Charges an outstanding item, by whichever of three methods the director picks.
 *
 * Separated from the dialog so the branch on payment route is not tangled with
 * the branch on which alert to render — the legacy `PayInvoice.tsx` mixed both
 * and grew to 870 lines.
 */
export const usePayItem = (item: OutstandingItem, onPaid: () => void): UsePayItem => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const { data: general } = useGetGeneralDataQuery(undefined);
  const { data: subscription, isLoading: isLoadingCards } = useGetSubscriptionQuery(undefined);
  const [payAnnualReview] = usePayAnnualReviewMutation();
  const [payInvoice] = usePayInvoiceMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [chosenCard, setChosenCard] = useState('');
  const [chosenMethod, setChosenMethod] = useState<PaymentMethod | undefined>(undefined);
  const [cardholderName, setCardholderName] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [bankTransfer, setBankTransfer] = useState<BankTransferDetails>({
    name: '',
    email: '',
  });

  const { cards, selectedCard } = resolveCards(subscription, chosenCard);
  const isCardDisabled = isSmsfCardDisabled(general) && isSuperFund(item);

  // SMSF forces bank transfer whatever was picked before the flag was known;
  // otherwise the director's choice stands, defaulting to a saved card when
  // they have one and a new card when they do not.
  const method: PaymentMethod = isCardDisabled
    ? 'bankTransfer'
    : (chosenMethod ?? (cards.length > 0 ? 'savedCard' : 'newCard'));

  const lines = useMemo(() => paymentLinesFor(item), [item]);
  const invoiceLinks = useMemo(() => invoiceLinksFor(item), [item]);
  const subtotal = totalOf(lines);
  const route = paymentRouteOf(item);

  const isAbsorbed = agentAbsorbsFee(item, agentPaysMerchantFee(general));
  const fees: MethodFees = {
    card: processingFeeOn(subtotal, 'newCard', isAbsorbed),
    bankTransfer: processingFeeOn(subtotal, 'bankTransfer', isAbsorbed),
  };
  const processingFee = method === 'bankTransfer' ? fees.bankTransfer : fees.card;
  const total = subtotal + processingFee;

  const completion = useMethodCompletion(method);
  const { isCardComplete, isBankAccountComplete } = completion;

  const isMethodReady = (): boolean => {
    if (method === 'savedCard') return selectedCard !== '';
    if (method === 'newCard') return cardholderName.trim() !== '' && isCardComplete;
    return (
      bankTransfer.name.trim() !== '' &&
      EMAIL_PATTERN.test(bankTransfer.email.trim()) &&
      isBankAccountComplete
    );
  };

  const canPay =
    isStripeConfigured &&
    stripe !== null &&
    elements !== null &&
    route !== undefined &&
    // Strictly greater: the backend answers "The total fee must be greater
    // than 0,5AUD", so a total of exactly the minimum is refused too.
    total > MINIMUM_CHARGE &&
    !isSubmitting &&
    isMethodReady();

  const charge = async (sourceId: string) => {
    // `isCardKept` is derived once and used for both: the flag the API is sent
    // and the `paymentMethod` string have to agree, and passing the raw
    // checkbox state to each let a stale tick from an abandoned new-card entry
    // reach one of them.
    const isCardKept = method === 'newCard' && saveCard;
    const details = {
      cardId: method === 'savedCard' ? selectedCard : '',
      sourceId,
      saveCard: isCardKept,
      paymentMethod: paymentMethodValueOf(method, isCardKept),
    };

    if (route === 'annualReview') {
      return payAnnualReview({ annualReviewId: item.annualReviewId, ...details }).unwrap();
    }
    return payInvoice({
      invoiceId: route === 'asicInvoice' ? item.id : (item.invoices[0]?.id ?? ''),
      hasAsicInvoice: route === 'asicInvoice',
      ...details,
    }).unwrap();
  };

  const pay = async () => {
    if (route === undefined || stripe === null || elements === null) return;

    setIsSubmitting(true);
    setError('');

    try {
      const sourceId =
        method === 'newCard' ? await tokeniseNewCard(stripe, elements, cardholderName) : '';

      const result = await charge(sourceId);

      // A bank transfer the backend left unsettled has to be confirmed here;
      // one it already settled must not be, or the director pays twice.
      if (method === 'bankTransfer' && needsClientConfirmation(result)) {
        await confirmBankTransfer(stripe, elements, result.client_secret ?? '', bankTransfer);
      }

      dispatch(
        toastShown(
          method === 'bankTransfer'
            ? 'Payment submitted. Bank transfers take up to 5 business days to clear.'
            : 'Payment submitted.',
          'success',
        ),
      );
      onPaid();
    } catch (caught) {
      // The legacy screen swallowed this into a commented-out console.error and
      // left the row looking unpaid with no explanation.
      setError(
        caught instanceof PaymentError
          ? caught.message
          : 'That payment could not be completed. Try another method, or contact your agent.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    pay,
    isSubmitting,
    error,
    cards,
    isLoadingCards,
    selectedCard,
    selectCard: setChosenCard,
    method,
    selectMethod: setChosenMethod,
    isCardDisabled,
    cardholderName,
    setCardholderName,
    saveCard,
    setSaveCard,
    bankTransfer,
    setBankTransfer,
    lines,
    invoiceLinks,
    subtotal,
    processingFee,
    total,
    fees,
    canPay,
    completion,
  };
};
